import { type ClassValue, clsx } from "clsx";
import { parse as parseCsv, unparse } from "papaparse";
import type { ZodSchema, z } from "zod";
import { twMerge } from "@remi/next-ui/utils";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const escapeHtml = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

export const parseCsvWithSchema = async <TOutput, TSchema extends ZodSchema<TOutput>>(
  csv: File,
  rowSchema: TSchema,
  transformHeader?: (header: string) => string,
) => {
  type Output = z.infer<TSchema>;
  return new Promise<{ rows: z.ZodSafeParseResult<Output>[]; fields: string[] }>((resolve, reject) => {
    parseCsv(csv, {
      header: true,
      skipEmptyLines: true,
      transformHeader,
      error: (err: unknown) => {
        reject(err);
      },
      complete: (results) => {
        resolve({ rows: results.data.map((row) => rowSchema.safeParse(row)), fields: results.meta.fields ?? [] });
      },
    });
  });
};

const downloadFile = (content: string, filename: string, fileType = "text/plain") => {
  const blob = new Blob([content], { type: fileType });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

export const downloadCsv = (data: string, filename: string) => {
  downloadFile(data, `${filename}.csv`, "text/csv");
};

const CSV_EXPORT_CHUNK_SIZE = 500;

export const chunkRowsForCsvExport = (
  rows: string[][],
  baseFilename: string,
): Array<{ filename: string; rows: string[][] }> => {
  const chunkCount = Math.max(1, Math.ceil(rows.length / CSV_EXPORT_CHUNK_SIZE));
  return Array.from({ length: chunkCount }, (_, index) => {
    const chunkRows = rows.slice(index * CSV_EXPORT_CHUNK_SIZE, (index + 1) * CSV_EXPORT_CHUNK_SIZE);
    const suffix = chunkCount > 1 ? `-${index + 1}` : "";
    return { filename: `${baseFilename}${suffix}`, rows: chunkRows };
  });
};

const MULTI_DOWNLOAD_INTERVAL_MS = 300;

export const downloadRowsAsChunkedCsv = async (rows: string[][], baseFilename: string): Promise<void> => {
  const chunks = chunkRowsForCsvExport(rows, baseFilename);
  for (const [index, chunk] of chunks.entries()) {
    downloadCsv(unparse(chunk.rows), chunk.filename);
    if (index < chunks.length - 1) {
      // Browsers drop programmatic downloads fired back-to-back in one tick; space them so each blob download starts
      await new Promise<void>((resolve) => setTimeout(resolve, MULTI_DOWNLOAD_INTERVAL_MS));
    }
  }
};

/**
 * Estimate project length
 */
export function calculateDaysPerSquare(squares: number): number {
  const minSquares = 0;
  const maxSquares = 100;
  const maxRate = 0.2;
  const minRate = 0.17;

  if (squares <= minSquares) {
    return maxRate;
  }
  if (squares >= maxSquares) {
    return minRate;
  }

  const normalized = squares / maxSquares;
  const eased = 1 - Math.pow(1 - normalized, 2); // slow progression

  return maxRate - eased * (maxRate - minRate);
}
